import type { Recipe } from '../types';

// Instant Pot carnitas: a 4 lb pork shoulder from the freezer, citrus and a
// cumin-oregano rub, 40 minutes at pressure, shredded, then crisped under the
// broiler. Freezes in its own juice and crisps again in a skillet on reheat.

export const porkCarnitas: Recipe = {
  slug: 'pork-carnitas',
  title: 'Pork Carnitas',
  meal: 'dinner',
  category: 'meal',
  types: ['taco', 'burrito', 'rice-bowl'],
  cuisines: ['mexican'],
  traits: ['freezer-friendly', 'bulk', 'make-ahead', 'high-protein', 'one-pot'],
  yield: 'about 10 cups — 10 servings',
  activeTime: '25 min',
  totalTime: '1 hr 40 min',
  freezerLife: '3–4 months',
  difficulty: 1,

  summary:
    "Mexican pulled pork, Instant Pot style. Cut a pork shoulder into chunks, rub it with cumin and oregano, squeeze oranges and a lime over it and pressure cook. Shred it, then broil it until the edges go brown and crispy. Tacos tonight, burritos and rice bowls from the freezer for weeks.",

  flavorNotes:
    "Rich, juicy pork with crispy, caramelized edges — the contrast between tender and crunchy is the whole dish. Savory and lightly citrusy from the orange, warm from cumin, earthy from oregano, not spicy at all. If it tastes flat, it needs salt; if it is soft all the way through, it needs more time under the broiler. Bright toppings (lime, cilantro, onion, salsa) cut the richness.",

  ingredients: [
    {
      id: 'pork-shoulder',
      name: 'Boneless pork shoulder (pork butt)',
      total: '4 lb',
      prep: 'thawed, cut into 2-inch chunks. Leave the fat on — it is flavor and it crisps the meat later',
      fromFreezer: true,
      uses: [{ step: 1, amount: '4 lb' }],
    },
    {
      id: 'salt',
      name: 'Salt',
      total: '2½ tsp',
      uses: [{ step: 1, amount: '2½ tsp' }],
    },
    {
      id: 'cumin',
      name: 'Ground cumin',
      total: '1 tbsp',
      uses: [{ step: 1, amount: '1 tbsp' }],
    },
    {
      id: 'oregano',
      name: 'Dried oregano',
      total: '2 tsp',
      uses: [{ step: 1, amount: '2 tsp' }],
    },
    {
      id: 'chili-powder',
      name: 'Chili powder, mild',
      total: '2 tsp',
      shop: 'Great Value chili powder, 2.5 oz',
      uses: [{ step: 1, amount: '2 tsp' }],
    },
    {
      id: 'smoked-paprika',
      name: 'Smoked paprika',
      total: '1 tsp',
      uses: [{ step: 1, amount: '1 tsp' }],
    },
    {
      id: 'pepper',
      name: 'Black pepper',
      total: '1 tsp',
      uses: [{ step: 1, amount: '1 tsp' }],
    },
    {
      id: 'onion',
      name: 'Yellow onion',
      total: '1 large',
      prep: 'cut into quarters',
      shop: 'Fresh yellow onion, each (1)',
      uses: [{ step: 2, amount: '1 large' }],
    },
    {
      id: 'garlic',
      name: 'Garlic',
      total: '6 cloves',
      prep: 'peeled and smashed with the side of a knife',
      uses: [{ step: 2, amount: '6 cloves' }],
    },
    {
      id: 'orange',
      name: 'Oranges',
      total: '2',
      prep: 'halved',
      shop: 'Navel oranges, each (2)',
      uses: [{ step: 2, amount: '2', note: 'juice squeezed in, rinds go in too' }],
    },
    {
      id: 'lime',
      name: 'Limes',
      total: '2',
      prep: 'one halved for the pot, one cut in wedges for serving',
      uses: [
        { step: 2, amount: '1', note: 'juice and rind into the pot' },
        { step: 6, amount: '1', note: 'wedges at the table' },
      ],
    },
    {
      id: 'water',
      name: 'Water',
      total: '½ cup',
      uses: [{ step: 2, amount: '½ cup' }],
    },
    {
      id: 'tortillas',
      name: 'Tortilla Land uncooked tortillas',
      total: '18',
      optional: true,
      optionalKind: 'serve-with',
      optionalWhy: 'For tacos or burritos. Raw — cook each in a dry skillet about 45 seconds a side until puffed and freckled.',
      uses: [{ step: 6, amount: '18', note: 'cooked in a dry skillet first' }],
    },
    {
      id: 'cilantro',
      name: 'Fresh cilantro',
      total: '1 bunch',
      prep: 'chopped',
      optional: true,
      optionalKind: 'garnish',
      optionalWhy: 'Fresh and bright against the rich pork. Gourmet Garden cilantro paste works if the store is out.',
      uses: [{ step: 6, amount: 'a handful', note: 'at the table' }],
    },
    {
      id: 'salsa',
      name: "Mateo's mild salsa",
      total: '1 jar',
      optional: true,
      optionalKind: 'garnish',
      optionalWhy: 'Heat and acid go on at the table so the pork itself stays mild for everyone.',
      uses: [{ step: 6, amount: 'to taste', note: 'at the table' }],
    },
  ],

  steps: [
    {
      n: 1,
      title: 'Season the pork',
      time: '10 min',
      ingredientIds: ['pork-shoulder', 'salt', 'cumin', 'oregano', 'chili-powder', 'smoked-paprika', 'pepper'],
      body:
        "Cut the {4 lb|pork-shoulder} of pork shoulder into 2-inch chunks and put them straight into the Instant Pot. Sprinkle on {2½ tsp|salt} of salt, {1 tbsp|cumin} of cumin, {2 tsp|oregano} of oregano, {2 tsp|chili-powder} of chili powder, {1 tsp|smoked-paprika} of smoked paprika and {1 tsp|pepper} of pepper, and toss with your hands until every piece is coated.",
      tip:
        "The pork must be thawed — you can't cut a frozen shoulder into chunks. Move it to the fridge 2 days ahead. Pressure cooking dulls ground spices, so this looks like a lot of seasoning. It isn't.",
    },
    {
      n: 2,
      title: 'Citrus, onion, garlic — then seal',
      time: '5 min',
      ingredientIds: ['onion', 'garlic', 'orange', 'lime', 'water'],
      body:
        "Tuck the onion quarters and smashed garlic between the pork. Squeeze the juice of {2|orange} oranges and {1|lime} lime over everything, then drop the squeezed rinds into the pot too. Pour in {½ cup|water} of water around the edge. Lock the lid and turn the valve to Sealing.",
    },
    {
      n: 3,
      title: 'Pressure cook, then natural release',
      time: '1 hr 5 min',
      critical: true,
      ingredientIds: [],
      body:
        "Pressure Cook on High for 40 minutes. It takes about 10 minutes to come up to pressure before the timer starts. When it beeps, let it do a natural release for 15 minutes, then turn the valve to Venting for the rest. The pork is done when a chunk falls apart when you press it with a fork.",
      tip:
        "If the pot flashes Burn, something is stuck to the bottom. Open it, scrape the floor, add ¼ cup of water and restart. Not tender yet? Seal it again for another 10 minutes.",
    },
    {
      n: 4,
      title: 'Shred and save the juice',
      time: '10 min',
      ingredientIds: [],
      body:
        "Fish out and toss the orange and lime rinds. Lift the pork onto a sheet pan with a slotted spoon and shred it with two forks. The onion and garlic will have melted — mash them in. Let the juice in the pot sit a few minutes, then skim the clear fat off the top into a small bowl and save it. Keep the juice too.",
    },
    {
      n: 5,
      title: 'Broil until crispy',
      time: '8 min',
      critical: true,
      ingredientIds: [],
      body:
        "Move an oven rack about 6 inches under the broiler and turn the broiler to high. Spread the pork in a thin layer on one or two sheet pans. Drizzle about {½ cup} of the juice and {2 tablespoons} of the saved fat over it. Broil 4 to 5 minutes until the tips are brown and crispy, stir it around, and broil 2 to 3 minutes more. Stand there and watch it the whole time — it goes from crispy to burnt fast.",
      tip:
        "Only crisp what you are eating today. Freeze the rest soft, in its juice, and crisp it at reheat — crisp pork that gets frozen goes chewy.",
    },
    {
      n: 6,
      title: 'Serve',
      ingredientIds: ['tortillas', 'cilantro', 'salsa', 'lime'],
      body:
        "Pile it into tortillas, over rice, or into burritos. Lime wedges, cilantro and salsa go on the table so everyone builds their own.",
    },
  ],

  freezing:
    "Cool completely. Pack about 2 cups of shredded (not yet crisped) pork per freezer bag with ¼ cup of the cooking juice poured over it, press flat, label and freeze up to 4 months. The juice keeps it from drying out. Freeze any extra juice in a small container — it is great in rice or beans. Throw out the hardened fat or save it in the fridge for crisping.",

  reheat: [
    {
      method: 'Skillet, crisped',
      best: true,
      detail:
        "Thaw overnight in the fridge. Heat a large skillet over medium-high with a teaspoon of the saved fat or oil. Spread the pork flat, press it down, and leave it alone 3 to 5 minutes until the bottom is brown and crispy. Flip it around and give it 1 more minute.",
    },
    {
      method: 'Broiler',
      detail:
        "Thawed pork spread on a sheet pan with its juice, broiled on high 4 to 6 minutes, stirring once. Best for a big batch.",
    },
    {
      method: 'Microwave',
      detail:
        "Covered with a splash of the juice, 2 to 3 minutes. Juicy and fast, but no crispy edges.",
    },
  ],

  glossary: [
    {
      term: 'pork shoulder',
      plain:
        "Also called pork butt or Boston butt (it is the shoulder, not the rear). A fatty, tough cut that turns soft and juicy after a long or pressure cook. Lean cuts like pork loin dry out here — don't swap.",
    },
    {
      term: 'natural release',
      plain:
        "Letting the Instant Pot lose pressure on its own instead of flipping the valve right away. It keeps the meat from tightening up and keeps it juicy.",
    },
    {
      term: 'skim',
      plain:
        "Spoon off the clear, golden layer of fat that floats on top of the juice. Save it — a little bit of it is what makes the pork crisp under the broiler.",
    },
    {
      term: 'broiler',
      plain:
        "The oven's top heating element on its highest setting. It browns food from above in a few minutes, like an upside-down grill.",
    },
  ],

  inspiration: [
    { label: 'Platings + Pairings — Instant Pot carnitas', url: 'https://www.platingsandpairings.com/instant-pot-carnitas/' },
    { label: 'Milk Street — Carnitas, fast or slow', url: 'https://archive.177milkstreet.com/recipes/carnitas-fast-slow' },
    { label: 'Skinnytaste — Instant Pot carnitas', url: 'https://www.skinnytaste.com/instant-pot-carnitas-mexican-pulled-pork/' },
  ],

  whereWeVeered:
    "Traditional carnitas simmer for hours in lard. We used the Instant Pot version that most home cooks rate highest: citrus and spices, 40 minutes on High, natural release, then the broiler. We skipped the garlic salt and basil some versions use and kept the spices to what is already in the pantry. Milk Street's skillet-crisping trick became the reheat method, and we freeze the pork soft in its juice instead of crisped, because pre-crisped pork turns chewy in the freezer.",
};
